// 1. Imports
import {
    Alert,
    Box,
    Collapse,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Button,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { When } from "react-if";
import { toast } from "react-toastify";
import Loading from "../../../loading";
import { RootState, store } from "../../../../store";
import { MessageCenterService } from "../../../../services/message-center.service";
import { ComposeSearchService } from "../../../../services/compose-search.service";
import { BrowseGroup, SearchUserItem } from "../../../../services/compose-search.types";
import { useDebounce } from "../../../../hooks/useDebounce";
import { SelectedRecipientsDisplay } from "./selected-recipients-display.component";
import { SelectAllSection } from "./select-all-section.component";
import { BrowseHierarchyComponent } from "./browse-hierarchy.component";
import { TagFilteringSection } from "./tag-filtering-section.component";
import { RecipientStatsBar } from "./recipient-stats-bar.component";
import { permissionCheck } from "../../../../helpers/permissionCheck";
import { customerPermissionFlags } from "../../../../constants/systemPermissions.constants";
import { GT_COLORS } from "../../../../constants/designTokens.constants";

// 2. Component Def
interface Props {
    mode: 'message' | 'reminder';
}

export const RecipientsComponentV2: React.FC<Props> = ({ mode }) => {
    // 2.1 State and Vars
    const navigate = useNavigate();
    const { id } = useParams();
    const X_PADDING = 7;
    const Y_PADDING = 1;

    // Redux selectors
    const { recipientCriteria, editMode } = useSelector((state: RootState) => state.messageCenter);
    const dispatch = store.dispatch.messageCenter;

    // Local state
    const [browseData, setBrowseData] = useState<BrowseGroup[]>([]);
    const [browseLoading, setBrowseLoading] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
    const [recipientCount, setRecipientCount] = useState<number | null>(null);
    const [countLoading, setCountLoading] = useState(false);

    const [orgFilterCount, setOrgFilterCount] = useState<number | null>(null);
    const [userDetailsMap, setUserDetailsMap] = useState<Map<string, SearchUserItem>>(new Map());
    const [userNamesLoading, setUserNamesLoading] = useState(false);
    // Ids already sent to the lookup endpoint, so an id it can't resolve
    // (deleted user, out of scope) isn't re-requested on every render.
    const lookedUpUserIds = useRef(new Set<string>());
    const [browseExpanded, setBrowseExpanded] = useState(false);

    // Debounce criteria for count endpoint
    const debouncedCriteria = useDebounce(recipientCriteria, 500);

    // Derived state
    const activeFilter = mode === 'reminder' ? 'active' : (recipientCriteria.activeFilter || 'all');
    const selectAllUsers = recipientCriteria.selectAllUsers || false;
    const isAdmin = permissionCheck(customerPermissionFlags.ADMIN);
    const scopeLabel = isAdmin ? 'in org' : 'in scope';

    const hasAnyCriteria =
        (recipientCriteria.groupIds?.length ?? 0) > 0 ||
        (recipientCriteria.teamIds?.length ?? 0) > 0 ||
        (recipientCriteria.userIds?.length ?? 0) > 0 ||
        recipientCriteria.selectAllUsers === true;

    // 2.2 Effects

    // Lock activeFilter for reminders
    useEffect(() => {
        if (mode === 'reminder') {
            dispatch.updateRecipientCriteria({ activeFilter: 'active' });
        }
    }, [mode]);

    // Fetch org-wide count for filter context label
    useEffect(() => {
        if (activeFilter === 'all' || mode === 'reminder') {
            setOrgFilterCount(null);
            return;
        }
        ComposeSearchService.count({ selectAllUsers: true, activeFilter: activeFilter as 'active' | 'inactive' })
            .then((res) => setOrgFilterCount(res.count))
            .catch(() => setOrgFilterCount(null));
    }, [activeFilter, mode]);

    // Load browse hierarchy
    useEffect(() => {
        setBrowseLoading(true);
        ComposeSearchService.browse(activeFilter as 'active' | 'inactive' | 'all')
            .then((data) => {
                setBrowseData(data);
                setExpandedGroups(data.map(g => g.id));
            })
            .catch(() => toast.error('Failed to load groups'))
            .finally(() => setBrowseLoading(false));
    }, [activeFilter]);

    // Initialize edit mode
    useEffect(() => {
        if (id && !editMode.isEditing) {
            if (mode === 'message') {
                MessageCenterService.initializeMessageEditMode(id, (isValid, message) => {
                    if (!isValid) {
                        toast.error(message || 'Failed to load message for editing');
                        navigate('/messages');
                    }
                });
            } else {
                MessageCenterService.initializeAutomatedReminderEditMode(id, (isValid, message) => {
                    if (!isValid) {
                        toast.error(message || 'Failed to load reminder for editing');
                        navigate('/messages');
                    }
                });
            }
        }
    }, [id, editMode.isEditing, mode]);

    // Resolve names for user ids that arrived without details — the recipients
    // saved on a message or reminder being edited. Users picked via search land
    // in userDetailsMap directly; saved ids only came with their id, so every
    // chip read "Name unavailable" (ticket 868m9nqx2).
    useEffect(() => {
        const missing = (recipientCriteria.userIds || []).filter(
            (uid) => !userDetailsMap.has(uid) && !lookedUpUserIds.current.has(uid)
        );
        if (missing.length === 0) return;
        missing.forEach((uid) => lookedUpUserIds.current.add(uid));

        setUserNamesLoading(true);
        ComposeSearchService.lookupUsers(missing)
            .then((users) => {
                if (users.length === 0) return;
                setUserDetailsMap((prev) => {
                    const next = new Map(prev);
                    users.forEach((u) => next.set(u.id, u));
                    return next;
                });
            })
            .catch(() => {
                // Leave the chips unresolved; allow a retry on the next change.
                missing.forEach((uid) => lookedUpUserIds.current.delete(uid));
            })
            .finally(() => setUserNamesLoading(false));
    }, [recipientCriteria.userIds, userDetailsMap]);

    // Live count via debounced criteria
    useEffect(() => {
        const debouncedHasCriteria =
            (debouncedCriteria.groupIds?.length ?? 0) > 0 ||
            (debouncedCriteria.teamIds?.length ?? 0) > 0 ||
            (debouncedCriteria.userIds?.length ?? 0) > 0 ||
            debouncedCriteria.selectAllUsers === true;

        if (!debouncedHasCriteria) {
            setRecipientCount(0);
            return;
        }

        setCountLoading(true);
        ComposeSearchService.count(debouncedCriteria)
            .then((res) => setRecipientCount(res.count))
            .catch(() => setRecipientCount(null))
            .finally(() => setCountLoading(false));
    }, [debouncedCriteria]);

    // 2.3 Functions

    const handleGroupToggle = (groupId: string) => {
        const currentGroupIds = recipientCriteria.groupIds || [];
        const isSelected = currentGroupIds.includes(groupId);

        if (isSelected) {
            dispatch.updateRecipientCriteria({
                groupIds: currentGroupIds.filter(id => id !== groupId),
            });
        } else {
            dispatch.updateRecipientCriteria({
                groupIds: [...currentGroupIds, groupId],
            });
        }
    };

    const handleTeamToggle = (teamId: string) => {
        const currentTeamIds = recipientCriteria.teamIds || [];
        const isSelected = currentTeamIds.includes(teamId);

        if (isSelected) {
            dispatch.updateRecipientCriteria({
                teamIds: currentTeamIds.filter(id => id !== teamId),
            });
        } else {
            dispatch.updateRecipientCriteria({
                teamIds: [...currentTeamIds, teamId],
            });
        }
    };

    const handleUserToggle = (userId: string) => {
        const currentUserIds = recipientCriteria.userIds || [];
        const isSelected = currentUserIds.includes(userId);

        if (isSelected) {
            dispatch.updateRecipientCriteria({
                userIds: currentUserIds.filter(id => id !== userId),
            });
        } else {
            dispatch.updateRecipientCriteria({
                userIds: [...currentUserIds, userId],
            });
        }
    };

    const handleSelectAllToggle = () => {
        if (!selectAllUsers) {
            // Turning on: clear individual selections
            dispatch.updateRecipientCriteria({
                selectAllUsers: true,
                groupIds: [],
                teamIds: [],
                userIds: [],
            });
        } else {
            dispatch.updateRecipientCriteria({
                selectAllUsers: false,
            });
        }
    };

    const handleActiveFilterChange = (value: 'active' | 'inactive' | 'all') => {
        dispatch.updateRecipientCriteria({ activeFilter: value });
    };

    const handleToggleExpand = (groupId: string) => {
        setExpandedGroups(prev =>
            prev.includes(groupId)
                ? prev.filter(id => id !== groupId)
                : [...prev, groupId]
        );
    };

    const handleBack = () => {
        if (editMode.isEditing) {
            dispatch.exitEditMode();
            dispatch.resetRecipientCriteria();
            navigate('/messages');
        } else {
            navigate('/messages');
        }
    };

    const handleContinue = () => {
        if (!hasAnyCriteria) {
            toast.error('Please select at least one recipient');
            return;
        }

        // Navigate based on mode and edit state
        if (mode === 'message') {
            if (editMode.isEditing && id) {
                navigate(`/messages/${id}/edit/compose`);
            } else {
                navigate('/messages/new/compose');
            }
        } else {
            if (editMode.isEditing && id) {
                navigate(`/messages/automated-reminders/${id}/edit/configure`);
            } else {
                navigate('/messages/automated-reminders/configure');
            }
        }
    };

    const getContinueLabel = (): string => {
        if (editMode.isEditing) return 'Continue Editing';
        return 'Compose Message';
    };

    const getBackLabel = (): string => {
        if (editMode.isEditing) return 'Cancel';
        return 'Back';
    };

    // 2.4 JSX Return
    return (
        <Box sx={{ position: "relative" }}>
            {/* Loading Overlay */}
            <When condition={browseLoading}>
                <Box sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 10,
                }}>
                    <Loading />
                </Box>
            </When>

            {/* Page Header */}
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: GT_COLORS.textPrimary, mb: 1 }}>
                Who Should Receive this Message?
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: GT_COLORS.textMuted, mb: 4 }}>
                {mode === 'reminder' ? 'Select recipients for automated reminder' : 'Select your recipients'}
            </Typography>

            {/* User Status Filter (messages only) */}
            <When condition={mode !== 'reminder'}>
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <ToggleButtonGroup
                            value={activeFilter}
                            exclusive
                            onChange={(_, value) => {
                                if (value) handleActiveFilterChange(value);
                            }}
                            size="small"
                            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', borderRadius: 1 } }}
                        >
                            <ToggleButton value="all" sx={{ px: X_PADDING, py: Y_PADDING, fontSize: '0.875rem' }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: GT_COLORS.textFaint, mr: 1 }} />
                                All Users
                            </ToggleButton>
                            <ToggleButton value="active" sx={{ px: X_PADDING, py: Y_PADDING, fontSize: '0.875rem' }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: GT_COLORS.green, mr: 1 }} />
                                Mobile App: Yes
                            </ToggleButton>
                            <ToggleButton value="inactive" sx={{ px: X_PADDING, py: Y_PADDING, fontSize: '0.875rem' }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: GT_COLORS.red, mr: 1 }} />
                                Mobile App: No
                            </ToggleButton>
                        </ToggleButtonGroup>

                        {orgFilterCount !== null && (
                            <Typography sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted, fontStyle: 'italic' }}>
                                {orgFilterCount} {activeFilter === 'active' ? 'mobile app' : 'no mobile app'} users {scopeLabel}
                            </Typography>
                        )}
                    </Box>

                    {activeFilter !== 'all' && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Showing {activeFilter === 'active' ? 'mobile app' : 'no mobile app'} users only — search results, browse counts, and recipient totals reflect only {activeFilter === 'active' ? 'mobile app' : 'no mobile app'} users.
                        </Alert>
                    )}
                </Box>
            </When>

            {/* To: label + Selected Recipients Display */}
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                To:
            </Typography>
            <SelectedRecipientsDisplay
                browseLoading={browseLoading}
                userNamesLoading={userNamesLoading}
                groupIds={recipientCriteria.groupIds || []}
                teamIds={recipientCriteria.teamIds || []}
                userIds={recipientCriteria.userIds || []}
                selectAllUsers={selectAllUsers}
                browseData={browseData}
                userDetailsMap={userDetailsMap}
                onRemoveGroup={handleGroupToggle}
                onRemoveTeam={handleTeamToggle}
                onRemoveUser={handleUserToggle}
                onDeselectAll={handleSelectAllToggle}
                recipientCount={recipientCount}
                activeFilter={activeFilter as 'active' | 'inactive' | 'all'}
                onSelectGroup={handleGroupToggle}
                onSelectTeam={handleTeamToggle}
                onSelectUser={(user) => {
                    setUserDetailsMap(prev => new Map(prev).set(user.id, user));
                    handleUserToggle(user.id);
                }}
            />

            {/* Select All + Browse Button */}
            <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                    <SelectAllSection
                        checked={selectAllUsers}
                        onToggle={handleSelectAllToggle}
                        mode={mode}
                    />
                </Box>
                <Button
                    variant="outlined"
                    onClick={() => setBrowseExpanded(prev => !prev)}
                    endIcon={browseExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    disabled={selectAllUsers}
                    sx={{
                        whiteSpace: 'nowrap',
                        px: 6,
                        textTransform: 'none',
                        borderRadius: 1,
                        color: GT_COLORS.primary,
                        borderColor: GT_COLORS.borderInput,
                    }}
                >
                    Browse Groups & Teams
                </Button>
            </Box>

            {/* Browse Hierarchy (collapsible) */}
            <Collapse in={browseExpanded}>
                <BrowseHierarchyComponent
                    browseData={browseData}
                    expandedGroups={expandedGroups}
                    onToggleExpand={handleToggleExpand}
                    selectedGroupIds={recipientCriteria.groupIds || []}
                    selectedTeamIds={recipientCriteria.teamIds || []}
                    onGroupToggle={handleGroupToggle}
                    onTeamToggle={handleTeamToggle}
                    disabled={selectAllUsers}
                />
            </Collapse>

            {/* Tag Filtering */}
            <TagFilteringSection
                    criteria={recipientCriteria}
                    onAddTagFilter={(filter) => {
                        const currentFilters = recipientCriteria.tagFilters || [];
                        dispatch.updateRecipientCriteria({
                            tagFilters: [...currentFilters, filter],
                        });
                    }}
                    onRemoveTagFilter={(filter) => {
                        const currentFilters = recipientCriteria.tagFilters || [];
                        dispatch.updateRecipientCriteria({
                            tagFilters: currentFilters.filter(
                                tf => !(tf.entityType === filter.entityType && tf.tagId === filter.tagId)
                            ),
                        });
                    }}
                    onSetTagLogic={(logic) => {
                        dispatch.updateRecipientCriteria({ tagLogic: logic });
                    }}
                />

            {/* Recipient Stats */}
            <RecipientStatsBar
                count={recipientCount}
                loading={countLoading}
                activeFilter={activeFilter as 'active' | 'inactive' | 'all'}
            />

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
                <Button
                    variant="outlined"
                    onClick={handleBack}
                    sx={{
                        minWidth: 100,
                        textTransform: 'none',
                        borderRadius: 1,
                        color: GT_COLORS.textMuted,
                        borderColor: GT_COLORS.borderInput,
                    }}
                >
                    {getBackLabel()}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleContinue}
                    disabled={!hasAnyCriteria}
                    sx={{
                        minWidth: 140,
                        textTransform: 'none',
                        borderRadius: 1,
                        backgroundColor: GT_COLORS.primary,
                        '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                    }}
                >
                    {getContinueLabel()}
                </Button>
            </Box>
        </Box>
    );
};
